import { useQuery } from '@tanstack/react-query'
import { searchActiveBillingFlowPickingBranches } from 'api/services/dispatchPickingServices'
import {
  dispatchBeforeInvoiceFulfillmentPlanIds,
  getDistributionCenterWarehouseIds,
} from 'Utils/constants'
import { QUERY_KEYS } from 'Utils/queryKeys'

const ELIGIBLE_FULFILLMENT_PLAN_IDS = Object.values(
  dispatchBeforeInvoiceFulfillmentPlanIds
)

const PAGE_SIZE = 100
// Safety cap so a backend bug (pages that never shrink below PAGE_SIZE)
// can't loop forever instead of just returning an incomplete result.
const MAX_PAGES = 50

// The search endpoint paginates; a branch beyond the first page would
// otherwise be silently missed, so this walks every page instead of
// trusting a single request to return everything.
const fetchAllActiveBillingFlowPickingBranches = async ({
  companyId,
  search,
}) => {
  let allBranches = []

  for (let pageNumber = 1; pageNumber <= MAX_PAGES; pageNumber++) {
    const response = await searchActiveBillingFlowPickingBranches({
      companyId,
      search,
      pageNumber,
      pageSize: PAGE_SIZE,
    })
    const branches = response?.Data ?? []
    allBranches = allBranches.concat(branches)

    if (branches.length < PAGE_SIZE) break
  }

  return { Data: allBranches }
}

export const useDispatchFlowEligibility = ({
  companyId,
  branchOfficeId,
  branchName,
  warehouseId,
  fulfillmentPlanId,
}) => {
  const query = useQuery({
    queryKey: [
      QUERY_KEYS.DISPATCH_BEFORE_INVOICE_ENABLED,
      companyId,
      branchOfficeId,
    ],
    queryFn: () =>
      fetchAllActiveBillingFlowPickingBranches({
        companyId,
        search: branchName || '',
      }),
    enabled: !!companyId && !!branchOfficeId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  })

  // GUIDs may come back in different casing depending on the source
  // (backend response vs. local branch/fulfillment-plan state), so all
  // comparisons below are done case-insensitively.
  const isBranchEnabled = !!query.data?.Data?.some(
    branch =>
      branch.BranchOfficeId?.toLowerCase() === branchOfficeId?.toLowerCase()
  )

  const isEligibleFulfillmentPlan =
    !!fulfillmentPlanId &&
    ELIGIBLE_FULFILLMENT_PLAN_IDS.some(
      id => id.toLowerCase() === fulfillmentPlanId.toLowerCase()
    )

  const isCashOnDeliveryPlan =
    fulfillmentPlanId?.toLowerCase() ===
    dispatchBeforeInvoiceFulfillmentPlanIds.cashOnDelivery.toLowerCase()

  const distributionCenterWarehouseIds = getDistributionCenterWarehouseIds()
  // Fail closed: if the D1 warehouse list isn't configured for this
  // environment, we can't confirm the dispatch warehouse is NOT a
  // distribution center, so cash-on-delivery stays blocked rather than
  // silently allowing "dispatch only" for it.
  const isCashOnDeliveryFromDistributionCenter =
    isCashOnDeliveryPlan &&
    (distributionCenterWarehouseIds.length === 0 ||
      distributionCenterWarehouseIds.some(
        id => id.toLowerCase() === warehouseId?.toLowerCase()
      ))

  const isOptionBEnabled =
    isBranchEnabled &&
    isEligibleFulfillmentPlan &&
    !isCashOnDeliveryFromDistributionCenter

  return {
    isLoading: query.isFetching,
    isOptionBEnabled,
  }
}
